import {
  WSTable,
  WSTableCell,
  WSTableColumnSortDirection,
  WSTableProps,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import {
  ClientWorkFlowStatus,
  IPayableSchema,
  IScheduleDate,
  InvoiceStatus
} from "@wingspanhq/payments/dist/interfaces";
import { PayeeRow } from "@wingspanhq/wingspan-api-sdk";
import React, { useMemo } from "react";
import { getIsPayableDisputed } from "../../../query/payments/selectors";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";
import { calculateLineItemsTotal } from "../../utils";
import { selectorPayableIcon } from "./selectorPayableIcon";

type TableItemData = {
  payeeRow?: PayeeRow;
  payable: IPayableSchema;
  payrollRun?: IScheduleDate;
};

type Props = {
  payables: IPayableSchema[];
  payeeRows: PayeeRow[];
  showInvoiceNumber?: boolean;
  showDateRequested?: boolean;
  showDueDate?: boolean;
  showDatePaid?: boolean;
  showHeaders?: boolean;
  sortDirection?: WSTableColumnSortDirection;
  onSort?: (sortDirection: WSTableColumnSortDirection) => void;
  showRefundedLabel?: boolean;
} & Omit<WSTableProps<TableItemData>, "tableData">;

export const TablePayables: React.FC<Props> = ({
  payables,
  payeeRows,
  showInvoiceNumber,
  showDateRequested,
  showDueDate,
  showDatePaid,
  showHeaders,
  sortDirection,
  onSort,
  showRefundedLabel,
  ...tableProps
}) => {
  const data = useMemo(
    () =>
      payables.map(payable => ({
        id: payable.payableId,
        mark:
          (payable.status === InvoiceStatus.Pending &&
            payable.client.workflowStatus === ClientWorkFlowStatus.Approved) ||
          getIsPayableDisputed(payable)
            ? ("amber400" as any)
            : undefined,
        data: {
          payable,
          payeeRow: payeeRows.find(row => row.id === payable.memberId)
        }
      })),
    [payables, payeeRows]
  );

  return (
    <WSTable<TableItemData>
      tableData={data}
      columns={[
        {
          config: {
            gridTemplateSizeMax: "2fr",
            header: showHeaders
              ? {
                  text: "Name"
                }
              : undefined
          },
          renderFunction: ({ data: { payable, payeeRow } }) => {
            if (!payeeRow) {
              return null;
            }

            return (
              <WSTableCell
                avatar={{ type: "icon", ...selectorPayableIcon(payable) }}
                text={selectorSearchRowName(payeeRow)}
                secondaryText={payeeRow.email ?? undefined}
              />
            );
          }
        },

        {
          config: {
            gridTemplateSizeMax: "1fr",
            justify: "start",
            hideOnScreens: showInvoiceNumber
              ? ["XS", "S"]
              : ["XS", "S", "M", "L", "XL"],
            header: showHeaders
              ? {
                  text: "Invoice number"
                }
              : undefined
          },
          renderFunction: ({ data: { payable } }) => (
            <WSTableCell text={payable?.invoiceNumber} />
          )
        },
        {
          config: {
            gridTemplateSizeMax: "1fr",
            hideOnScreens: showInvoiceNumber
              ? ["XS", "S"]
              : ["XS", "S", "M", "L", "XL"],
            header: showHeaders
              ? {
                  text: "Description"
                }
              : undefined
          },
          renderFunction: ({ data: { payable } }) => {
            return <WSTableCell text={payable.lineItems[0].description} />;
          }
        },
        {
          config: {
            gridTemplateSizeMax: "1fr",
            hideOnScreens: showDateRequested
              ? ["XS", "S"]
              : ["XS", "S", "M", "L", "XL"],
            header: showHeaders
              ? {
                  text: "Date requested"
                }
              : undefined
          },
          renderFunction: ({ data: { payable } }) =>
            payable?.status === InvoiceStatus.Pending ? (
              <WSTableCell text="Eligibility pending" />
            ) : (
              <WSTableCell
                text={toWSDateString(payable?.events.openedAt, "monthDayYear")}
              />
            )
        },
        {
          config: {
            gridTemplateSizeMax: "1fr",
            justify: "start",
            hideOnScreens: showDueDate
              ? ["XS", "S"]
              : ["XS", "S", "M", "L", "XL"],
            header: showHeaders
              ? {
                  text: "Pay date"
                }
              : undefined
          },
          renderFunction: ({ data: { payable } }) => (
            <WSTableCell
              text={toWSDateString(
                payable?.client.payDate || payable?.dueDate,
                "monthDayYear"
              )}
              secondaryText={payable?.client.payDate ? "Updated" : ""}
            />
          )
        },
        {
          config: {
            gridTemplateSizeMax: "1fr",
            hideOnScreens: showDatePaid
              ? ["XS", "S"]
              : ["XS", "S", "M", "L", "XL"],
            header: showHeaders
              ? {
                  text: "Date paid"
                }
              : undefined,
            sortDirection,
            onColumnSort: onSort
          },
          renderFunction: ({ data: { payable } }) => (
            <WSTableCell
              text={toWSDateString(payable?.events.paidAt, "monthDayYear")}
            />
          )
        },
        {
          config: {
            gridTemplateSizeMax: "1fr",
            justify: "start",
            hideOnScreens: showRefundedLabel
              ? ["XS", "S"]
              : ["XS", "S", "M", "L", "XL"],
            onColumnSort: onSort
          },
          renderFunction: ({ data: { payable } }) => (
            <WSTableCell
              pill={
                payable.events.refundedAt ? { text: "Refunded" } : undefined
              }
            />
          )
        },
        {
          config: {
            gridTemplateSizeMin: "min-content",
            gridTemplateSizeMax: "1fr",
            header: showHeaders
              ? {
                  text: "Amount"
                }
              : undefined
          },
          renderFunction: ({ data: { payable } }) => (
            <WSTableCell
              text={formatMoney(
                calculateLineItemsTotal(payable.lineItems) +
                  (payable.chargedFees?.lateFee?.amount || 0),
                payable.currency
              )}
            />
          )
        }
      ]}
      data-testid="payablesTable"
      {...tableProps}
    />
  );
};
